import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { CONSTANTS } from '../../services/constants';
import { useTheme } from '@mui/material/styles';
import CustomHeader from '../training/CustomHeader';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme as baseStyles } from '../../tailwind.config';
import { getTextColorClass } from '../../services/generalUtils';

const TabsHeader = ({
  title,
  description,
  backgroundImage,
  useGradient,
  backgroundColor,
  titleStyle,
  descriptionStyle,
}) => {
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isBetweenSmMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isBetweenMdLg = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [backgroundStyle, setBackgroundStyle] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const [displayedTitle, setDisplayedTitle] = useState(String(title || ''));
  const [displayedDescription, setDisplayedDescription] = useState(
    String(description || ''),
  );

  useEffect(() => {
    if (title !== displayedTitle || description !== displayedDescription) {
      setIsVisible(false);
      const updateTimeout = setTimeout(() => {
        setDisplayedTitle(title);
        setDisplayedDescription(description);
      }, 270);
      const fadeInTimeout = setTimeout(() => {
        setIsVisible(true);
      }, 30);

      return () => {
        clearTimeout(updateTimeout);
        clearTimeout(fadeInTimeout);
      };
    }
  }, [title, description, displayedTitle, displayedDescription]);

  useEffect(() => {
    let newBackgroundStyle = {};
    const gradientWithColor = `linear-gradient(180deg, rgba(0, 71, 129, 0.62) 0%, ${backgroundColor} 50%)`;
    const gradientWithImage = `linear-gradient(0deg,rgba(0, 21, 33, 1) 0%, rgba(0, 54, 119, 0.68) 100%)`;

    newBackgroundStyle.background = useGradient
      ? gradientWithColor
      : backgroundColor;

    if (backgroundImage && backgroundImage.url) {
      if (useGradient) {
        newBackgroundStyle.backgroundImage = `url("${backgroundImage.url}"), ${gradientWithImage}`;
      } else {
        newBackgroundStyle.backgroundImage = `url("${backgroundImage.url}")`;
      }
    }
    setBackgroundStyle(newBackgroundStyle);
  }, [backgroundImage, useGradient, backgroundColor]);

  let fontSize, lineHeight, paddingTop, fontWeight, letterSpacing;
  if (isDownSm) {
    fontSize = titleStyle?.fontSize || '28px';
    lineHeight = titleStyle?.lineHeight || '44px';
    paddingTop = titleStyle?.paddingTop || '32px';
  } else if (isBetweenSmMd) {
    fontSize = titleStyle?.fontSize || '48px';
    lineHeight = titleStyle?.lineHeight || '64px';
    paddingTop = titleStyle?.paddingTop || '44px';
  } else if (isBetweenMdLg) {
    fontSize = titleStyle?.fontSize || '48px';
    lineHeight = titleStyle?.lineHeight || '64px';
    paddingTop = titleStyle?.paddingTop || '54px';
  } else if (isUpLg) {
    fontWeight = titleStyle?.fontWeight || 600;
    fontSize = titleStyle?.fontSize || '64px';
    lineHeight = titleStyle?.lineHeight || '80px';
    letterSpacing = titleStyle?.letterSpacing || '0%';
    paddingTop = titleStyle?.paddingTop || '100px';
  } else {
    fontSize = titleStyle?.fontSize || '64px';
    lineHeight = titleStyle?.lineHeight || '80px';
    paddingTop = titleStyle?.paddingTop || '32px';
  }

  return (
    <HeaderWrapper backgroundStyle={backgroundStyle}>
      <ContentWrapper isVisible={isVisible}>
        <CustomHeader
          text={displayedTitle}
          headerStyle={{
            level: titleStyle?.level,
            color: getTextColorClass(titleStyle?.color),
            alignment: 'text-left',
            fontWeight,
            fontSize,
            lineHeight,
            letterSpacing,
            paddingTop,
            className: 'tab-title',
            position: 'relative',
            width: '100%',
            zIndex: 2,
          }}
          data-testid="tab-title"
        />
        <StyledTabDescription
          descriptionTextColor={
            descriptionStyle?.textColor || theme.palette.common.white
          }
          alignment={descriptionStyle?.alignment || CONSTANTS.ALIGNMENT.LEFT}
          descriptionStyle={descriptionStyle}
          data-testid="tab-description"
        >
          {displayedDescription}
        </StyledTabDescription>
      </ContentWrapper>
    </HeaderWrapper>
  );
};

const ContentWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})(({ theme }) => ({
  transition: 'opacity 0.3s ease-in-out',
  opacity: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.between('sm', 'md')]: {
    gap: '5px',
  },
  variants: [
    {
      props: ({ isVisible }) => isVisible,
      style: {
        opacity: 1,
      },
    },
  ],
}));

const HeaderWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'backgroundStyle',
})(({ theme, backgroundStyle }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  justifyContent: 'left',
  textAlign: 'left',
  gap: '24px',
  width: '100%',
  maxWidth: '1332px',
  height: '100%',
  minHeight: '283px',
  paddingLeft: '26px',
  [theme.breakpoints.down('sm')]: {
    width: '400px',
    height: '283x',
    paddingLeft: '26px',
    paddingRight: '26px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: '630px',
    height: '300px',
    paddingLeft: '26px',
    paddingRight: '26px',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    width: '748px',
    height: '380px',
    paddingLeft: '36px',
    paddingRight: '36px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '1332px',
    height: '480px',
    paddingLeft: '72px',
    paddingRight: '72px',
  },
  zIndex: 1,
  ...backgroundStyle,
}));

const StyledTabDescription = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'descriptionStyle' && prop !== 'descriptionTextColor',
})(({ theme, descriptionTextColor, alignment, descriptionStyle }) => ({
  color: descriptionTextColor || theme.palette.primary.main,
  fontFamily: 'Source Sans Pro',
  fontWeight: 400,
  fontSize: '24px',
  lineHeight: '44px',
  letterSpacing: '0%',
  listSpacing: '12px',
  textAlign: alignment || 'left',
  position: 'relative',
  height: '148px',
  paddingTop: '50px',
  [theme.breakpoints.down('sm')]: {
    width: '326px',
    height: '100px',
    fontSize: '16px',
    lineHeight: '24px',
    paddingTop: '0px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: '600px',
    height: '120px',
    fontSize: '20px',
    lineHeight: '32px',
    paddingTop: '10px',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    width: '600px',
    height: '120px',
    fontSize: '20px',
    lineHeight: '32px',
    paddingTop: '30px',
  },
  variants: [
    {
      props: ({ descriptionStyle }) => descriptionStyle,
      style: {
        ...descriptionStyle,
        color: descriptionStyle?.textColor || descriptionTextColor,
        fontSize: descriptionStyle?.fontSize || baseStyles.fontSize,
        lineHeight: descriptionStyle?.lineHeight || baseStyles.lineHeight,
      },
    },
  ],
}));

export default TabsHeader;
